/*
 * Matches the Shop "Sign in with Shop" button to the width of the Add to Cart
 * button directly above it on the product page.
 *
 * Why this needs JavaScript
 * ------------------------
 * The button is a Shopify app block (the Shop app) that renders a <button>
 * inside its own shadow DOM, styled `width:auto; margin:auto`. Theme CSS cannot
 * cross a shadow boundary, and the component exposes no ::part() hooks, so the
 * only reachable levers are (a) inherited custom properties and (b) a
 * stylesheet appended into the shadow root — which is possible only because the
 * Shop app opens its root. This does (b), and sizes the button from
 * --pdp-cta-height so section-main-product.css stays the single source of truth
 * for CTA geometry.
 *
 * Fail-safe: the host stays at `width:fit-content` (its left-aligned natural
 * size, i.e. the pre-change rendering) unless the injected rule is *verified* to
 * have reached the button — see the rollback in match(). So if the Shop app
 * restructures its shadow tree, the button reverts to its old appearance rather
 * than sitting at natural width inside an empty 38rem host.
 *
 * This file is inside <section id="MainProduct-…">, and the quick-add modal
 * re-executes every script tag it clones (assets/quick-add.js). The init guard
 * below keeps that from stacking a document-level listener and a fresh polling
 * chain on every modal open.
 */
(function () {
  if (window.__pdpCtaMatchInit) return;
  window.__pdpCtaMatchInit = true;

  // Scoped to the section tag, which quick-add does not clone — so the modal's
  // copy of the button keeps Craft's defaults. See section-main-product.css.
  var HOST_SELECTOR = '.main-product-section shop-login-button';
  var MATCHED_CLASS = 'pdp-cta-matched';
  var STYLE_MARKER = 'data-pdp-cta-match';

  // Scoped to the top-level button, so any secondary control the Shop app may
  // render inside its shadow tree is left alone — this rule is !important
  // inside their tree and would be unfixable from outside.
  //
  // `height`, not `min-height`: the Shop button's natural height (54px) is
  // *taller* than Add to Cart's (47px), so min-height would never bring it
  // down and the pair would stay mismatched. Safe because the Shop app marks
  // its label `whitespace-nowrap`, so there is no wrap to clip.
  var SHADOW_CSS = ':host > button{width:100%!important;height:var(--pdp-cta-height)!important;}';

  var POLL_INTERVAL = 100;
  var POLL_TIMEOUT = 10000;
  var timer = null;

  function match(host) {
    var root = host.shadowRoot;
    if (!root) return false;

    // The element upgrades and populates asynchronously; no button yet means
    // the Shop app has not finished rendering, so report "not done" and retry.
    var button = root.querySelector('button');
    if (!button) return false;
    if (root.querySelector('style[' + STYLE_MARKER + ']')) return true;

    var style = document.createElement('style');
    style.setAttribute(STYLE_MARKER, '');
    style.textContent = SHADOW_CSS;
    root.appendChild(style);
    host.classList.add(MATCHED_CLASS);

    // Confirm the rule actually landed on the button before leaving the host
    // widened. Reading layout here forces a synchronous style recalc, so the
    // measurement reflects the rule just injected. If a future version of the
    // Shop app nests the button and the selector misses, roll the whole thing
    // back — the button keeps its natural size and so does the host.
    if (button.getBoundingClientRect().width < host.getBoundingClientRect().width - 1) {
      host.classList.remove(MATCHED_CLASS);
      style.remove();
    }
    return true;
  }

  function matchAll() {
    var hosts = document.querySelectorAll(HOST_SELECTOR);
    // No host on this page (merchant removed the app block) is a terminal
    // state, not something to keep polling for.
    if (!hosts.length) return true;

    var done = true;
    for (var i = 0; i < hosts.length; i++) {
      if (!match(hosts[i])) done = false;
    }
    return done;
  }

  function poll() {
    if (timer) clearTimeout(timer);
    var waited = 0;

    (function tick() {
      timer = null;
      if (matchAll()) return;
      waited += POLL_INTERVAL;
      if (waited < POLL_TIMEOUT) timer = setTimeout(tick, POLL_INTERVAL);
    })();
  }

  poll();

  // The theme editor re-renders the section on every setting change, which
  // replaces the host element and drops the injected stylesheet with it.
  document.addEventListener('shopify:section:load', poll);
})();
